import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { Fragment, type ReactNode } from "react";
import East from "@mui/icons-material/East";
import dayjs from "dayjs";
import { createSingleDeliveryText } from "./deliveryText";
import type { TransportInformation as TruckInfo } from "../types";
import utc from "dayjs/plugin/utc.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import "dayjs/locale/de.js";
import {
  formatHistoryArticle,
  formatHistoryValue,
  historyFieldLabel,
} from "./orderHistoryFormatting";
import type { HistoryChange, HistoryValue } from "./orderHistoryFormatting";
import { plantStatusLabels } from "./plantOrderFormatting";

dayjs.extend(utc);
dayjs.extend(relativeTime);

// The BOB history layout is shared; each application supplies its own fetched entries.
const formatter = (key: string, value: HistoryValue): string => {
  if (key === "plantStatus" && typeof value === "string" && value)
    return value === "CRITICAL"
      ? "Kritisch"
      : (plantStatusLabels[value] ?? value);
  if (key === "transportStatus" && typeof value === "string" && value)
    return (
      (
        {
          OPEN: "Neue Bestellung",
          SENT: "Gesehen",
          ACCEPTED: "Akzeptiert",
          CHANGED: "Geändert",
          MOVED: "Verschoben",
          RETRACTED: "Zurückgezogen",
          STORNO: "Storniert",
          DELETED: "Gelöscht",
        } as Record<string, string>
      )[value] ?? value
    );
  return formatHistoryValue(key, value);
};
// Old events can contain null logistics before the first truck was added.
const transportLines = (value: HistoryValue) => {
  if (value === null) return [];
  if (!Array.isArray(value))
    return [formatHistoryValue("transportInformation", value)];
  return value.map((truck) => {
    if (
      !truck ||
      typeof truck !== "object" ||
      Array.isArray(truck) ||
      typeof truck.type !== "string" ||
      typeof truck.amount !== "number"
    ) {
      return formatHistoryValue("transportInformation", truck);
    }
    return createSingleDeliveryText(truck as unknown as TruckInfo, true);
  });
};

const TransportInfoDisplay = ({
  change,
  lines,
}: {
  change: [HistoryValue, HistoryValue];
  lines: (value: HistoryValue) => ReactNode[];
}) => {
  const old = lines(change[0]);
  const newx = lines(change[1]);
  const oldData = Array.isArray(change[0]) ? change[0] : [];
  const newData = Array.isArray(change[1]) ? change[1] : [];

  return (
    <div className="changesets csarray">
      <div className="changesigs cs_trucks">
        {old.length === 0
          ? "keine"
          : old.map((line, i) => (
              <div
                key={i}
                className={
                  "changesigs_line" +
                  (newx.length <= i
                    ? " cs_deleted"
                    : JSON.stringify(newData[i]) !== JSON.stringify(oldData[i])
                      ? " cs_changed"
                      : "")
                }
              >
                {line}
              </div>
            ))}
      </div>
      <East className="chagarro" fontSize="inherit" />
      <div className="changesigs cs_trucks">
        {newx.length === 0
          ? "keine"
          : newx.map((line, i) => (
              <div
                key={i}
                className={
                  "changesigs_line" +
                  (old.length <= i
                    ? " cs_created"
                    : JSON.stringify(oldData[i]) !== JSON.stringify(newData[i])
                      ? " cs_changed"
                      : "")
                }
              >
                {line}
              </div>
            ))}
      </div>
    </div>
  );
};

const ChangeDisplay = ({
  name,
  change,
  formatValue,
  transportLines,
}: {
  name: string;
  change: HistoryChange;
  formatValue: (key: string, value: HistoryValue) => string;
  transportLines: (value: HistoryValue) => ReactNode[];
}) => {
  const format =
    name === "article"
      ? formatHistoryArticle
      : (value: HistoryValue) => formatValue(name, value);
  return Array.isArray(change) && change.length === 2 ? (
    name === "transportInformation" ? (
      <TransportInfoDisplay
        change={change as [HistoryValue, HistoryValue]}
        lines={transportLines}
      />
    ) : (
      <div className="changesets csarray">
        <div className="changesigs">{format(change[0])}</div>
        <East className="chagarro" fontSize="inherit" />
        <div className="changesigs">{format(change[1])}</div>
      </div>
    )
  ) : (
    <div className="changesets cssingleval">{format(change)}</div>
  );
};

export interface OrderHistoryEntry {
  who: string | { firstName?: string; name?: string } | null;
  when: string;
  what: Record<string, HistoryChange>;
}

export function OrderHistoryDialog({
  open = true,
  entries,
  loading = false,
  error,
  formatValue = formatter,
  fieldLabel = historyFieldLabel,
  onClose,
}: {
  open?: boolean;
  entries: OrderHistoryEntry[];
  loading?: boolean;
  error?: ReactNode;
  formatValue?: (key: string, value: HistoryValue) => string;
  fieldLabel?: (key: string) => string;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={onClose}
      aria-labelledby="order-history-title"
    >
      {loading ? (
        <CircularProgress />
      ) : error ? (
        error
      ) : (
        <Fragment>
          <DialogTitle
            id="order-history-title"
            sx={{
              p: 1,
              px: 2,
              background: "#002169",
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.5)",
            }}
          >
            Änderungshistorie
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {entries.length === 0 ? (
              "Keine nachverfolgten Änderungen gefunden."
            ) : (
              <div className="changes_container">
                {entries.map((x, i) => (
                  <div className="singleChange" key={i}>
                    <div className="changeHeader">
                      <div className="changedatehea">
                        <Tooltip
                          title={
                            dayjs
                              .utc(x.when, "YYYY-MM-DD HH:mm:ss")
                              .local()
                              .format("ddd DD.MM.YYYY, HH:mm:ss") + " Uhr"
                          }
                        >
                          <span>
                            {dayjs
                              .utc(x.when, "YYYY-MM-DD HH:mm:ss")
                              .local()
                              .fromNow()}
                          </span>
                        </Tooltip>
                      </div>
                      <div className="changeuserhead">
                        {(typeof x.who === "string"
                          ? x.who
                          : [x.who?.firstName, x.who?.name]
                              .filter(Boolean)
                              .join(" ")) || "Unbekannter Benutzer"}
                      </div>
                    </div>
                    <div className="changechanges">
                      {Object.entries(x.what).map(([key, change]) => (
                        <div className="singchange" key={key}>
                          <div className="skekyna">{fieldLabel(key)}</div>
                          <ChangeDisplay
                            name={key}
                            change={change}
                            formatValue={formatValue}
                            transportLines={transportLines}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid #aaa",
              justifyContent: "space-between",
            }}
          >
            <div />
            <Button onClick={onClose}>Schließen</Button>
          </DialogActions>
        </Fragment>
      )}
    </Dialog>
  );
}
